import type { RouteLocationNormalized, Router } from 'vue-router';

import isEqual from 'lodash/isEqual';

interface NavigationTracker {
  /** The in-flight navigation that will leave the current route, if there is one. */
  pending: RouteLocationNormalized | null;
  /** Targets whose `beforeEach` we saw, so `afterEach` can tell ours from a stranger's. */
  started: WeakSet<RouteLocationNormalized>;
  settled: Set<() => void>;
}

const trackers = new WeakMap<Router, NavigationTracker>();

/** A route target, reduced to what decides whether navigating between two of them is a real move. */
export type RouteTarget = Pick<RouteLocationNormalized, 'name' | 'params'>;

/**
 * Whether two targets are the same page. Query differences do not count — a query-only navigation
 * is not a route change.
 */
export function isSameRoute(a: RouteTarget, b: RouteTarget): boolean {
  return a.name === b.name && isEqual(a.params, b.params);
}

/**
 * Track whether a route change is in flight.
 *
 * Vue Router exposes no "navigation pending" flag, and `useRoute()` still reports the route being
 * left until the new one is confirmed — so anything that writes to the router mid-navigation
 * (`router.replace({ query })`, say) targets the old route and aborts the navigation the user
 * asked for. See sc-209211.
 *
 * Register this **before every other global guard**: `beforeEach` hooks run in registration order,
 * so an earlier async guard would hold this one back past the moment it is needed.
 *
 * Query-only navigations are deliberately not counted — those are the writes this exists to
 * protect, and treating them as pending would make a rapid second write drop itself.
 */
export function trackPendingNavigation(router: Router): void {
  const tracker: NavigationTracker = { pending: null, started: new WeakSet(), settled: new Set() };
  trackers.set(router, tracker);

  router.beforeEach((to, from) => {
    tracker.started.add(to);
    tracker.pending = isSameRoute(to, from) ? null : to;
  });

  router.afterEach((to) => {
    // A navigation cancelled by a newer one still reaches `afterEach`, and it gets there first —
    // clearing on that would reopen the window while the newer one is still in flight. Targets we
    // never saw start (a guard redirect that resolves to the current route, say) do clear it, so a
    // navigation whose `afterEach` never arrives cannot wedge this on past the next route change.
    if (to !== tracker.pending && tracker.started.has(to)) return;
    tracker.pending = null;

    const callbacks = [...tracker.settled];
    tracker.settled.clear();
    callbacks.forEach((callback) => callback());
  });
}

/** False when tracking was never installed — a router mock in unit tests, for instance. */
export function isNavigationPending(router: Router): boolean {
  return trackers.get(router)?.pending != null;
}

/**
 * Run `callback` once, when the navigation currently in flight settles — landed or aborted.
 *
 * Only meaningful while {@link isNavigationPending} is true; otherwise the callback waits for the
 * next route change. Returns an unregister function for callers that can go away first.
 */
export function onNavigationSettled(router: Router, callback: () => void): () => void {
  const tracker = trackers.get(router);
  if (!tracker) return () => {};

  tracker.settled.add(callback);
  return () => tracker.settled.delete(callback);
}
